import { type KeyboardEvent, useRef } from "react";

import { cn } from "@/lib/utils";

export type ColumnTabId = "highlights" | "investigation";

export type ColumnTab = {
  id: ColumnTabId;
  label: string;
  /** Shown beside the label when set. */
  count?: number;
};

export const columnTabId = (id: ColumnTabId) => `column-tab-${id}`;

// Tabs for the right-hand column, which has a fixed height: extra material a
// project carries sits behind a tab instead of under the highlights. Rendered
// only when there is more than the highlights to show.
export default function ColumnTabs({
  tabs,
  active,
  panelId,
  onChange,
}: {
  tabs: ColumnTab[];
  active: ColumnTabId;
  panelId: string;
  onChange: (id: ColumnTabId) => void;
}) {
  const buttons = useRef<
    Partial<Record<ColumnTabId, HTMLButtonElement | null>>
  >({});

  // Arrow keys move between tabs, as for any tab list.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.id === active);
    const next =
      event.key === "ArrowRight"
        ? (index + 1) % tabs.length
        : event.key === "ArrowLeft"
        ? (index - 1 + tabs.length) % tabs.length
        : event.key === "Home"
        ? 0
        : event.key === "End"
        ? tabs.length - 1
        : -1;
    if (next === -1) return;
    event.preventDefault();
    onChange(tabs[next].id);
    buttons.current[tabs[next].id]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Session material"
      onKeyDown={onKeyDown}
      className="flex shrink-0 gap-5 border-b border-muted px-3 dark:border-muted/20"
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={(button) => {
              buttons.current[tab.id] = button;
            }}
            id={columnTabId(tab.id)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cn(
              "-mb-px flex items-center gap-1.5 border-b-2 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              selected
                ? "border-accent font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="text-xs tabular-nums text-muted-foreground">
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
